// JOBE-0044.10: SchemaFieldPicker — two-step. Step 1 lists the
// per-type scalar fields read from the EntityTypeRow.scalarFields
// registry entry; step 2 opens a ScalarValuePopover for the value.
// When the type row doesn't carry `scalarFields`, falls back to a
// free-text "field name" input.

import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { EntityTypesContext } from '../../EntityTypesContext.js';
import { ScalarValuePopover } from './ScalarValuePopover.jsx';

export function SchemaFieldPicker({
  activeType,
  initialValues = [],
  initialFieldName = '',
  onSubmit,
  onCancel,
}) {
  const ctx = useContext(EntityTypesContext);
  const typeRow = useMemo(() => {
    if (!activeType) return null;
    const list = Array.isArray(ctx.types) ? ctx.types : [];
    return list.find((t) => t.kind === activeType) || null;
  }, [ctx.types, activeType]);

  const fields = Array.isArray(typeRow?.scalarFields) ? typeRow.scalarFields : null;

  const [step, setStep] = useState(initialFieldName ? 'value' : 'field');
  const [fieldName, setFieldName] = useState(initialFieldName || '');
  const inputRef = useRef(null);

  useEffect(() => {
    if (step === 'field') inputRef.current?.focus();
  }, [step]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancel) onCancel();
    }
  };

  const pickField = (name) => {
    if (!name) return;
    setFieldName(name);
    setStep('value');
  };

  if (step === 'value') {
    return (
      <ScalarValuePopover
        initialValues={initialValues}
        placeholder={`value for ${fieldName}`}
        onCancel={onCancel}
        onSubmit={(values) => {
          if (onSubmit) onSubmit({ fieldName, values });
        }}
      />
    );
  }

  return (
    <div
      className="library-composer-popover library-composer-schema"
      role="dialog"
      aria-label="Pick a schema field"
      onKeyDown={onKeyDown}
    >
      <div className="library-composer-popover-body">
        {fields && fields.length > 0 ? (
          <ul className="library-composer-field-list">
            {fields.map((f) => (
              <li key={f.name}>
                <button
                  type="button"
                  className="library-composer-field-row"
                  onClick={() => pickField(f.name)}
                >
                  <span className="library-composer-field-name">{f.name}</span>
                  <span className="library-composer-field-type">{f.type}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <div className="library-composer-field-freeform">
            <label>
              field name
              <input
                ref={inputRef}
                type="text"
                className="library-composer-scalar-input"
                placeholder="amount"
                value={fieldName}
                onChange={(e) => setFieldName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    pickField(fieldName.trim());
                  }
                }}
                aria-label="Field name"
              />
            </label>
            <button
              type="button"
              className="library-composer-submit"
              onClick={() => pickField(fieldName.trim())}
              disabled={!fieldName.trim()}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
